import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Modal,
  Dimensions,
  TextInput,
  SafeAreaView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as WebBrowser from 'expo-web-browser';
import { claimCoupon, getMyCoupons, updateCouponVoucherUrl, completeCashback, getDiscountText, getCouponImageSource, isProductGiftCoupon, getHadiahProdukValidationCode, buildClaimedCouponFromClaimResponse, isDigitalRewardCoupon, isDigitalRewardPending, isDigitalRewardAwaitingPhone, isDigitalRewardFailed, isDigitalRewardProcessing, getDigitalRewardSn, getDigitalRewardDestForm, refreshDigitalRewardClaim, completeDigitalRewardClaim } from '../services/couponsApi';
import { getPaygiftClaimModalUi, isPaygiftClaimPending } from '../utils/paygiftUserFacingMessage';
import { usePaygiftClaimBlockedModal } from '../components/PaygiftClaimBlockedModal';
import { navigateToClaimedCouponDetails } from '../navigation/navigateToClaimedCoupon';
import { generateAutoLoginUrl } from '../services/authApi';
import AppHeader from '../components/AppHeader';
import { API_BASE_URL } from '../config/api';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Clipboard from 'expo-clipboard';
import QRCode from 'react-native-qrcode-svg';
import CashierBarcode from '../components/CashierBarcode';
import { sanitizePhoneInput, maskPhoneDisplay } from '../utils/phoneInput';
import { detectMobileOperator } from '../utils/mobileOperator';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { useFocusEffect } from '@react-navigation/native';

const { width, height } = Dimensions.get('window');

/** Quiet poll while Digiprosb voucher is still processing (callback / status lag). */
const DIGITAL_REWARD_POLL_MS = 5000;
const DIGITAL_REWARD_POLL_MAX = 36; // ~3 minutes at 5s

const HEXPOD_THEME = {
  'primary': '#E46B29',
  'secondary': '#FDBA74',
  'tertiary': '#FFF7ED'
}
const LINKAJA_THEME = {
  'primary': '#EF4444',
  'secondary': '#F87171',
  'tertiary': '#FEE2E2'
}

const isCashbackCoupon = (coupon) => (coupon?.tipe_kupon || coupon?.type) === 'cashback';

const isCashbackPending = (coupon) =>
  !!coupon?.cashback_pending ||
  (isCashbackCoupon(coupon) && coupon?.external_ref_id === 'CASHBACK_PENDING');

const formatCashbackAmount = (coupon) => {
  const amount = Number(coupon?.discountValue ?? coupon?.nilai_diskon ?? 0);
  if (!amount || Number.isNaN(amount)) return '';
  return `Rp ${amount.toLocaleString('id-ID')}`;
};

const CouponDetailsScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { coupon: initialCoupon } = route.params;
  const { showFromError: showPaygiftBlockedFromError, modal: paygiftBlockedModal } = usePaygiftClaimBlockedModal();
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();
  const [coupon, setCoupon] = useState(initialCoupon);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [linkajaNumber, setLinkajaNumber] = useState('');
  const [cashbackSubmitting, setCashbackSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rewardPhone, setRewardPhone] = useState(
    () => sanitizePhoneInput(initialCoupon?.suggested_phone || '')
  );
  const [rewardSubmitting, setRewardSubmitting] = useState(false);
  const [pollExhausted, setPollExhausted] = useState(false);
  const couponRef = useRef(coupon);
  const pollCountRef = useRef(0);
  const snReadyNotifiedRef = useRef(!!getDigitalRewardSn(initialCoupon));
  const quietRefreshRef = useRef(null);

  useEffect(() => {
    couponRef.current = coupon;
  }, [coupon]);

  const rewardOperator = useMemo(
    () => detectMobileOperator(rewardPhone),
    [rewardPhone]
  );

  const cashbackTheme = useMemo(() => {
    if( coupon.is_hexpod_reward ) {
      return HEXPOD_THEME;
    }
    return LINKAJA_THEME;
  }, [coupon])

  const checkAuthStatus = async () => {
    try {
      const token = await AsyncStorage.getItem('userToken');
      setIsAuthenticated(!!token);
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  const checkCouponClaimStatus = async () => {
    try {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        console.log('No token found, skipping claim status check');
        return; // Skip if not authenticated
      }
      
      // console.log('Checking claim status for coupon ID:', coupon.id);
      console.log('Checking claim status for coupon');
      const myCouponsResponse = await getMyCoupons();
      // console.log('My coupons response:', myCouponsResponse);
      
      let isClaimed = coupon.is_claimed;
      let claimId = coupon.claim_id;
      
      // Handle the actual API response structure: {code: 200, status: 'success', data: {coupons: [...]}}
      if (myCouponsResponse && myCouponsResponse.data && myCouponsResponse.data.coupons && Array.isArray(myCouponsResponse.data.coupons)) {
        const claimedCoupons = myCouponsResponse.data.coupons;
        const claimedCouponIds = claimedCoupons.map(claimedCoupon => claimedCoupon.coupon?.id || claimedCoupon.coupon_id);
        // console.log('Claimed coupon IDs:', claimedCouponIds);
        isClaimed = claimedCouponIds.includes(coupon.id.toString());
        // console.log('Is coupon claimed?', isClaimed, 'Current state:', coupon.is_claimed);
        
        // Match by claim_id first so each hadiah kejutan row shows its own status (not the first/latest from API)
        const currentClaimId = coupon.claim_id || coupon.claimId;
        const matchByClaimId = (c) => String(c.claim_id) === String(currentClaimId);
        const matchByCouponId = (c) => (c.coupon?.id || c.coupon_id) === coupon.id.toString();
        
        // Try to find the claim_id from the response if not already set
        if (!claimId && isClaimed) {
          const claimedCoupon = currentClaimId
            ? claimedCoupons.find(c => matchByClaimId(c))
            : claimedCoupons.find(c => matchByCouponId(c));
          if (claimedCoupon && claimedCoupon.claim_id) {
            claimId = claimedCoupon.claim_id;
            console.log('Found claim_id from response:', claimId);
            
            // Also check if this is a paygift coupon with null voucher_url that needs updating
            if (claimedCoupon.tipe_kupon === 'paygift' && (!claimedCoupon.voucher_url || claimedCoupon.voucher_url === null)) {
              console.log('Paygift coupon with null voucher_url found in response');
            }
          }
        }
        
        // Always update coupon with data from API: prefer matching by claim_id so status is per-row
        const claimedCoupon = currentClaimId
          ? claimedCoupons.find(c => matchByClaimId(c))
          : claimedCoupons.find(c => matchByCouponId(c));
        if (claimedCoupon) {
          const resolvedClaimId = claimId || claimedCoupon.claim_id || coupon.claim_id || coupon.claimId;
          // console.log('Updating coupon with data from API (claim_id match):', claimedCoupon);
          console.log('Updating coupon with data from API');
          setCoupon(prevCoupon => {
            const resolvedSn =
              claimedCoupon.reward_sn
              ?? claimedCoupon.coupon_code
              ?? prevCoupon.reward_sn
              ?? prevCoupon.kode_kupon
              ?? prevCoupon.code
              ?? null;
            const snReady = !!getDigitalRewardSn({
              ...prevCoupon,
              ...claimedCoupon,
              reward_sn: resolvedSn,
              kode_kupon: resolvedSn,
              code: resolvedSn,
              coupon_code: resolvedSn,
              tipe_kupon: claimedCoupon.tipe_kupon || prevCoupon.tipe_kupon,
            });
            return {
              ...prevCoupon,
              is_claimed: isClaimed,
              claim_id: resolvedClaimId,
              claimId: resolvedClaimId,
              // Update all important fields from the API response (status per row for hadiah_kejutan)
              voucher_url: claimedCoupon.voucher_url || prevCoupon.voucher_url,
              status: claimedCoupon.status ?? prevCoupon.status,
              expiry_date: claimedCoupon.expiry_date || prevCoupon.expiry_date,
              voucher_expiry_date: claimedCoupon.voucher_expiry_date || prevCoupon.voucher_expiry_date,
              tipe_kupon: claimedCoupon.tipe_kupon || prevCoupon.tipe_kupon,
              type: claimedCoupon.tipe_kupon || prevCoupon.type || prevCoupon.tipe_kupon,
              nama_batch: claimedCoupon.nama_batch ?? prevCoupon.nama_batch,
              validation_code: claimedCoupon.validation_code ?? claimedCoupon.external_ref_id ?? prevCoupon.validation_code,
              external_ref_id: claimedCoupon.external_ref_id ?? prevCoupon.external_ref_id,
              cashback_pending: claimedCoupon.cashback_pending ?? prevCoupon.cashback_pending,
              cashback_disbursed: claimedCoupon.cashback_disbursed ?? prevCoupon.cashback_disbursed,
              cashback_phone_display: claimedCoupon.cashback_phone_display ?? prevCoupon.cashback_phone_display,
              reference_no: claimedCoupon.reference_no ?? prevCoupon.reference_no,
              reward_pending: snReady ? false : (claimedCoupon.reward_pending ?? prevCoupon.reward_pending),
              reward_awaiting_phone: snReady ? false : (claimedCoupon.reward_awaiting_phone ?? prevCoupon.reward_awaiting_phone),
              reward_processing: snReady ? false : (claimedCoupon.reward_processing ?? prevCoupon.reward_processing),
              reward_failed: snReady ? false : (claimedCoupon.reward_failed ?? prevCoupon.reward_failed),
              reward_failure_message: snReady ? null : (claimedCoupon.reward_failure_message ?? prevCoupon.reward_failure_message),
              reward_sn: snReady ? (resolvedSn || claimedCoupon.reward_sn || prevCoupon.reward_sn) : (claimedCoupon.reward_sn ?? prevCoupon.reward_sn),
              suggested_phone: claimedCoupon.suggested_phone ?? prevCoupon.suggested_phone,
              tampil_barcode: claimedCoupon.tampil_barcode ?? prevCoupon.tampil_barcode,
              digiprosb_dest_type: claimedCoupon.digiprosb_dest_type ?? prevCoupon.digiprosb_dest_type,
              dest_form: claimedCoupon.dest_form ?? prevCoupon.dest_form,
              kode_kupon: claimedCoupon.coupon_code
                ?? claimedCoupon.coupon?.kode_kupon
                ?? prevCoupon.kode_kupon,
              code: claimedCoupon.coupon_code
                ?? claimedCoupon.coupon?.kode_kupon
                ?? prevCoupon.code,
              coupon_code: claimedCoupon.coupon_code ?? prevCoupon.coupon_code,
              voucher_details: snReady
                ? {
                    ...(prevCoupon.voucher_details || {}),
                    sn: resolvedSn || prevCoupon.voucher_details?.sn,
                    pending: false,
                    awaiting_phone: false,
                    processing: false,
                    failed: false,
                    failure_message: null,
                  }
                : prevCoupon.voucher_details,
              kawan_activation: claimedCoupon.kawan_activation != null
                ? claimedCoupon.kawan_activation
                : prevCoupon.kawan_activation,
            };
          });
          if (claimedCoupon.suggested_phone && !rewardPhone) {
            setRewardPhone(sanitizePhoneInput(claimedCoupon.suggested_phone));
          }
        } else if (isClaimed !== coupon.is_claimed) {
          // If no matching coupon found but claim status changed, update basic info
          // console.log('Updating coupon claim status to:', isClaimed);
          console.log('Updating coupon claim status');
          setCoupon(prevCoupon => ({ ...prevCoupon, is_claimed: isClaimed, claim_id: claimId || prevCoupon.claim_id }));
        }
      } else {
        // console.log('Unexpected response format:', myCouponsResponse);
        console.log('Unexpected response format');
      }
      
      // After updating claim status, check if we need to update voucher URL
      // console.log('Calling checkAndUpdateVoucherUrl with:', { isClaimed, claimId, couponId: coupon.id, tipe_kupon: coupon.tipe_kupon, voucher_url: coupon.voucher_url });
      console.log('Calling checkAndUpdateVoucherUrl');
      await checkAndUpdateVoucherUrl(isClaimed, claimId);
      
    } catch (error) {
      console.error('Error checking coupon claim status:', error);
    }
  };

  // Check and update voucher URL for paygift coupons with null voucher_url
  const checkAndUpdateVoucherUrl = async (isClaimed, claimId) => {
    try {
      // Use provided claimId or fall back to coupon.claim_id
      const kuponDetailId = claimId || coupon.claim_id;
      
      // Check if conditions are met: coupon is claimed, type is paygift, and voucher_url is null
      // The coupon object has 'type' property instead of 'tipe_kupon'
      const couponType = coupon.type || coupon.coupon?.tipe_kupon || coupon.tipe_kupon;
      const voucherUrl = coupon.voucher_url;
      
      // console.log('Checking voucher URL update conditions:', { 
      //   isClaimed, 
      //   coupon_type: couponType, 
      //   voucher_url: voucherUrl,
      //   coupon_id: coupon.id,
      //   coupon_structure: coupon
      // });
      console.log('Checking voucher URL update conditions');
      
      if (isClaimed && 
          couponType === 'paygift' && 
          (!voucherUrl || voucherUrl === null)) {
        
        console.log('Paygift coupon with null voucher_url detected, attempting to update...');
        
        // Check if we have the required claim_id (kuponDetailId)
        if (kuponDetailId) {
          console.log('Updating voucher URL for coupon detail:', kuponDetailId);
          
          const result = await updateCouponVoucherUrl(kuponDetailId);
          
          if (result.success && result.voucher_url) {
            console.log('Voucher URL updated successfully:', result.voucher_url);
            // Update the coupon state with the new voucher URL
            setCoupon(prevCoupon => ({
              ...prevCoupon,
              voucher_url: result.voucher_url,
              voucher_expiry_date: result.expiry_date
            }));
          } else {
            console.log('Voucher URL update failed:', result.message);
          }
        } else {
          console.log('Missing claim_id (kuponDetailId) for voucher URL update');
        }
      }
    } catch (error) {
      console.error('Error updating voucher URL:', error);
      // Don't show error to user as this is a background process
    }
  };

  const handleCompleteCashback = async () => {
    const claimId = coupon.claim_id || coupon.claimId;
    const phone = linkajaNumber.trim();
    if (!phone) {
      showCustomAlert('Nomor LinkAja', 'Masukkan nomor handphone yang terdaftar di aplikasi LinkAja.');
      return;
    }
    if (phone.length < 10) {
      showCustomAlert('Nomor LinkAja', 'Nomor handphone minimal 10 digit (contoh: 08xxxxxxxxxx).');
      return;
    }
    if (!claimId) {
      showCustomAlert('Error', 'Data klaim tidak ditemukan. Muat ulang halaman kupon.');
      return;
    }

    setCashbackSubmitting(true);
    try {
      const result = await completeCashback(claimId, phone);
      setCoupon(prev => ({
        ...prev,
        cashback_pending: false,
        cashback_disbursed: true,
        reference_no: result?.data?.reference_no ?? prev.reference_no,
        external_ref_id: result?.data?.reference_no ?? prev.external_ref_id,
        cashback_phone_display:
          result?.data?.cashback_phone_display
          || maskPhoneDisplay(phone)
          || prev.cashback_phone_display,
      }));
      setLinkajaNumber('');
      showCustomAlert(
        'Cashback Berhasil',
        result?.message || 'Cashback LinkAja berhasil dikirim ke nomor yang kamu masukkan.',
        'success'
      );
    } catch (error) {
      showCustomAlert('Cashback Gagal', error.message || 'Gagal memproses cashback LinkAja.');
    } finally {
      setCashbackSubmitting(false);
    }
  };

  const applyDigitalRewardPayload = (data = {}) => {
    const sn = data.reward_sn || null;
    const done = !!sn;
    setCoupon((prev) => ({
      ...prev,
      reward_pending: done ? false : !!data.reward_pending,
      reward_awaiting_phone: done ? false : !!data.reward_awaiting_phone,
      reward_processing: done ? false : !!data.reward_processing,
      reward_failed: done ? false : !!data.reward_failed,
      reward_failure_message: done ? null : (data.reward_failure_message || null),
      reward_sn: sn,
      suggested_phone: data.suggested_phone || prev.suggested_phone,
      tampil_barcode: data.tampil_barcode ?? prev.tampil_barcode,
      digiprosb_dest_type: data.digiprosb_dest_type || prev.digiprosb_dest_type,
      dest_form: data.dest_form || prev.dest_form,
      kode_kupon: sn || (data.reward_pending ? null : prev.kode_kupon),
      code: sn || (data.reward_pending ? null : prev.code),
      coupon_code: sn || (data.reward_pending ? null : prev.coupon_code),
      voucher_details: {
        ...(prev.voucher_details || {}),
        sn,
        pending: done ? false : !!data.reward_pending,
        awaiting_phone: done ? false : !!data.reward_awaiting_phone,
        processing: done ? false : !!data.reward_processing,
        failed: done ? false : !!data.reward_failed,
        failure_message: done ? null : (data.reward_failure_message || null),
        external_ref_id: data.external_ref_id || prev.voucher_details?.external_ref_id,
      },
    }));
    if (data.suggested_phone && !rewardPhone) {
      setRewardPhone(sanitizePhoneInput(data.suggested_phone));
    }
  };

  const handleCompleteDigitalReward = async () => {
    const claimId = coupon.claim_id || coupon.claimId;
    const destForm = getDigitalRewardDestForm(coupon);
    const phone = String(rewardPhone || '').trim();

    if (!phone) {
      showCustomAlert('Data tujuan', 'Data tujuan wajib diisi.');
      return;
    }
    if (phone.length < (destForm.min_length || 3)) {
      showCustomAlert(
        'Data tujuan',
        `Data tujuan minimal ${destForm.min_length || 3} karakter.`
      );
      return;
    }
    if (!claimId) {
      showCustomAlert('Error', 'Data klaim tidak ditemukan. Muat ulang halaman kupon.');
      return;
    }

    setRewardSubmitting(true);
    try {
      const result = await completeDigitalRewardClaim(claimId, phone);
      applyDigitalRewardPayload(result?.data || {});
      if (result?.data?.reward_sn) {
        snReadyNotifiedRef.current = true;
      } else {
        pollCountRef.current = 0;
        setPollExhausted(false);
      }
      await checkCouponClaimStatus();
      showCustomAlert(
        result?.data?.reward_sn ? 'Berhasil' : 'Diproses',
        result?.message
          || (result?.data?.reward_sn
            ? 'Voucher berhasil diproses.'
            : 'Voucher sedang diproses. Status akan diperbarui otomatis.'),
        'success'
      );
    } catch (error) {
      if (error?.data) {
        applyDigitalRewardPayload(error.data);
      }
      showCustomAlert('Gagal', error.message || 'Gagal memproses voucher. Coba lagi.');
    } finally {
      setRewardSubmitting(false);
    }
  };

  const handleRefreshDigitalReward = async ({ quiet = false } = {}) => {
    const claimId = couponRef.current?.claim_id || couponRef.current?.claimId;
    if (!claimId || (!quiet && refreshing)) return;

    if (!quiet) setRefreshing(true);
    try {
      const result = await refreshDigitalRewardClaim(claimId);
      const data = result?.data || {};
      applyDigitalRewardPayload(data);

      if (quiet) {
        if (data.reward_sn && !snReadyNotifiedRef.current) {
          snReadyNotifiedRef.current = true;
          showCustomAlert(
            'Berhasil',
            result?.message || 'Voucher sudah siap.',
            'success'
          );
        }
        return;
      }

      await checkCouponClaimStatus();

      if (data.reward_failed) {
        showCustomAlert(
          'Gagal',
          data.reward_failure_message || result?.message || 'Voucher gagal diproses. Periksa data tujuan lalu coba lagi.'
        );
      } else if (data.reward_awaiting_phone) {
        showCustomAlert(
          'Lengkapi data',
          result?.message || 'Masukkan data tujuan sesuai deskripsi kupon untuk memproses voucher.'
        );
      } else {
        if (data.reward_sn) {
          snReadyNotifiedRef.current = true;
        }
        showCustomAlert(
          data.reward_sn ? 'Berhasil' : 'Masih diproses',
          data.reward_sn
            ? (result?.message || 'Voucher sudah siap.')
            : (result?.message || 'Voucher masih diproses. Coba refresh lagi sebentar.'),
          'success'
        );
      }
    } catch (error) {
      if (!quiet) {
        showCustomAlert('Error', error.message || 'Gagal memperbarui status voucher');
      }
    } finally {
      if (!quiet) setRefreshing(false);
    }
  };

  quietRefreshRef.current = handleRefreshDigitalReward;

  // Quiet poll while Digiprosb SN is still pending
  useFocusEffect(
    useCallback(() => {
      pollCountRef.current = 0;
      setPollExhausted(false);
      const id = setInterval(() => {
        const c = couponRef.current || {};
        if (!isDigitalRewardCoupon(c) || !isDigitalRewardProcessing(c) || getDigitalRewardSn(c)) {
          return;
        }
        const claimId = c.claim_id || c.claimId;
        if (!claimId) return;
        if (pollCountRef.current >= DIGITAL_REWARD_POLL_MAX) {
          setPollExhausted(true);
          return;
        }
        pollCountRef.current += 1;
        quietRefreshRef.current?.({ quiet: true });
      }, DIGITAL_REWARD_POLL_MS);
      return () => clearInterval(id);
    }, [])
  );

  const handleClaimCoupon = async () => {
    if (!isAuthenticated) {
      showCustomAlert(
        'Login Required',
        'Please login to claim this coupon',
        'error',
        () => navigation.navigate('Login')
      );
      return;
    }

    setIsLoading(true);
    try {
      const isIndobest = coupon.isIndobest || false;
      const response = await claimCoupon(coupon.id, isIndobest);
      const claimedCoupon = buildClaimedCouponFromClaimResponse(response, coupon);
      setCoupon(claimedCoupon);
      const isPending = isPaygiftClaimPending(claimedCoupon);
      const modalUi = isPending ? getPaygiftClaimModalUi(claimedCoupon) : null;
      showCustomAlert(
        isPending ? modalUi.title : 'Success',
        isPending ? modalUi.headerMessage : (response.message || 'Coupon claimed successfully!'),
        'success',
        () => navigateToClaimedCouponDetails(navigation, claimedCoupon)
      );
    } catch (error) {
      if (showPaygiftBlockedFromError(error)) {
        return;
      }
      showCustomAlert('Error', error.message || 'Failed to claim coupon');
    } finally {
      setIsLoading(false);
    }
  };


  const handleCopyCouponCode = async (couponCode) => {
    try {
      await Clipboard.setStringAsync(couponCode);
      showCustomAlert('Success', 'Coupon code copied to clipboard!', 'success');
    } catch (error) {
      console.error('Error copying coupon code:', error);
      showCustomAlert('Error', 'Failed to copy coupon code.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const couponImageSource = getCouponImageSource(coupon);

  useFocusEffect(
    React.useCallback(()=>{
      checkAuthStatus();
    }, [navigation])
  );

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader
        title="Detail Kupon"
        onBack={() => navigation.navigate('MainTabs', { screen: 'Coupons' })}
        right={
          coupon.is_claimed && isDigitalRewardCoupon(coupon) ? (
            <TouchableOpacity
              onPress={handleRefreshDigitalReward}
              disabled={refreshing}
              hitSlop={8}
            >
              {refreshing ? (
                <ActivityIndicator size="small" color="#94A3B8" />
              ) : (
                <Ionicons name="refresh" size={22} color="#1E293B" />
              )}
            </TouchableOpacity>
          ) : null
        }
      />
      <ScrollView style={styles.container}>

      {/* Image Container */}
      <View style={styles.imageContainer}>
        {couponImageSource ? (
          <Image source={couponImageSource} style={styles.couponImage} />
        ) : (
          <View style={styles.imageContent}>
            {/* Coupon Info */}
            <View style={styles.couponInfo}>
              
            </View>
            
            {/* Decorative Elements */}
            <View style={styles.decorativeCircle} />
           
          </View>
        )}
      </View>

      {/* Content */}
      <View style={styles.content}>
        {/* Badges */}
        <View style={styles.badgesContainer}>
          {isProductGiftCoupon(coupon) && coupon.is_claimed && getHadiahProdukValidationCode(coupon) && (
            <View style={[styles.badge, { backgroundColor: '#FEF3C7' }]}>
              <Ionicons name="shield-checkmark" size={16} color="#92400E" />
              <Text style={[styles.badgeText, { color: '#92400E' }]}>
                Kode validasi: {getHadiahProdukValidationCode(coupon)}
              </Text>
            </View>
          )}
          {coupon.isIndobest && (
            <View style={[styles.badge, styles.indobestBadge]}>
              <Ionicons name="flash" size={16} color="#F59E0B" />
              <Text style={[styles.badgeText, styles.indobestBadgeText]}>Indobest</Text>
            </View>
          )}
          {!coupon.is_claimed && (
            <View style={styles.badge}>
              <Ionicons name="receipt" size={16} color="#059669" />
              <Text style={styles.badgeText}>Tersisa {coupon.kuota_tersisa || coupon.remainingQuota || 'Unlimited'} voucher</Text>
            </View>
          )}
          
        </View>

        {/* Title */}
        <Text style={styles.title}>{coupon.nama_kupon || coupon.title || 'Coupon Title'}</Text>

        {/* Details */}
        <View style={styles.detailsContainer}>
          <View style={styles.detailItem}>
            <View style={styles.detailHeader}>
              <Ionicons name="time-outline" size={20} color="#6B7280" />
              <Text style={styles.detailLabel}>Berlaku hingga</Text>
            </View>
            <Text style={styles.detailValue}>{formatDate(coupon.tanggal_berakhir || coupon.endDate || coupon.valid_until)}</Text>
          </View>

          {(coupon.maksimal_diskon || 0) > 0 && (
            <View style={styles.detailItem}>
              <View style={styles.detailHeader}>
                <Ionicons name="card-outline" size={20} color="#6B7280" />
                <Text style={styles.detailLabel}>Maksimal diskon</Text>
              </View>
              <Text style={styles.detailValue}>{formatCurrency(coupon.maksimal_diskon)}</Text>
            </View>
          )}

          {coupon.maksimal_per_user && (
            <View style={styles.detailItem}>
              <View style={styles.detailHeader}>
                <Ionicons name="person-outline" size={20} color="#6B7280" />
                <Text style={styles.detailLabel}>Maksimal per pengguna</Text>
              </View>
              <Text style={styles.detailValue}>{coupon.maksimal_per_user} kali</Text>
            </View>
          )}

          {coupon.nama_kategori && (
            <View style={styles.detailItem}>
              <View style={styles.detailHeader}>
                <Ionicons name="pricetag-outline" size={20} color="#6B7280" />
                <Text style={styles.detailLabel}>Kategori</Text>
              </View>
              <Text style={styles.detailValue}>{coupon.nama_kategori}</Text>
            </View>
          )}
        </View>

        {/* Description */}
        <View style={styles.sectionContainer}>
          <View style={styles.sectionHeader}>
            <Ionicons name="document-text-outline" size={20} color="#6B7280" />
            <Text style={styles.sectionTitle}>Deskripsi</Text>
          </View>
          <Text style={styles.sectionContent}>
            {coupon.deskripsi || coupon.description || 'Tidak ada deskripsi'}
          </Text>
        </View>

        {coupon.is_claimed && isProductGiftCoupon(coupon) && getHadiahProdukValidationCode(coupon) && (
          <View style={styles.couponCodeContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="shield-checkmark-outline" size={20} color="#6B7280" />
              <Text style={styles.sectionTitle}>Kode Validasi Pemenang</Text>
            </View>
            <View style={styles.couponCodeBadge}>
              <Text style={styles.couponCodeText}>{getHadiahProdukValidationCode(coupon)}</Text>
            </View>
            <Text style={styles.qrCodeHint}>
              CS Hexpod akan menghubungi Anda. Pastikan nomer WA Anda sudah sesuai dengan yang terdaftar di zoolyfe club / Hexpod.
            </Text>
          </View>
        )}

        {coupon.is_claimed && isCashbackPending(coupon) && (
          <View style={[styles.cashbackPendingContainer, {backgroundColor: cashbackTheme.tertiary, borderColor: cashbackTheme.secondary}]}>
            <View style={styles.sectionHeader}>
              <Ionicons name="wallet-outline" size={20} color={cashbackTheme.primary} />
              <Text style={styles.sectionTitle}>Lengkapi Cashback LinkAja</Text>
            </View>
            <Text style={styles.cashbackPendingDesc}>
              Klaim reward sudah tercatat. Masukkan nomor LinkAja yang terdaftar di aplikasi untuk menerima cashback.
            </Text>
            <TextInput
              style={styles.linkajaInput}
              placeholder="08xxxxxxxxxx"
              placeholderTextColor="#9CA3AF"
              keyboardType="number-pad"
              inputMode="numeric"
              maxLength={15}
              value={linkajaNumber}
              onChangeText={(text) => setLinkajaNumber(sanitizePhoneInput(text))}
              editable={!cashbackSubmitting}
            />
            <TouchableOpacity
              style={[styles.cashbackSubmitBtn, {backgroundColor: cashbackTheme.primary}, cashbackSubmitting && styles.claimButtonDisabled]}
              onPress={handleCompleteCashback}
              disabled={cashbackSubmitting}
            >
              {cashbackSubmitting ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.cashbackSubmitBtnText}>Kirim Cashback LinkAja</Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {coupon.is_claimed && isCashbackCoupon(coupon) && !isCashbackPending(coupon) && (
          <View style={styles.cashbackSuccessContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="checkmark-circle-outline" size={20} color="#059669" />
              <Text style={styles.sectionTitle}>Saldo LinkAja terkirim</Text>
            </View>
            <Text style={styles.cashbackSuccessDesc}>
              {formatCashbackAmount(coupon)
                ? `Saldo ${formatCashbackAmount(coupon)} sudah masuk ke akun LinkAja-mu. `
                : 'Saldo LinkAja sudah masuk ke akunmu. '}
              Buka aplikasi LinkAja untuk memastikan saldo sudah masuk.
            </Text>
            {coupon.cashback_phone_display ? (
              <Text style={styles.cashbackRef}>Saldo diproses ke nomor {coupon.cashback_phone_display}</Text>
            ) : null}
          </View>
        )}

        {coupon.is_claimed && isDigitalRewardCoupon(coupon) && !getDigitalRewardSn(coupon) && (isDigitalRewardAwaitingPhone(coupon) || isDigitalRewardFailed(coupon)) && (
          <View style={[styles.cashbackPendingContainer, { backgroundColor: '#FFF7ED', borderColor: '#FDBA74' }]}>
            {isDigitalRewardFailed(coupon) && !!coupon.reward_failure_message ? (
              <Text style={[styles.cashbackPendingDesc, { marginBottom: 12 }]}>
                {coupon.reward_failure_message}
              </Text>
            ) : null}
            <Text style={[styles.cashbackPendingDesc, { marginBottom: 8 }]}>
              {getDigitalRewardDestForm(coupon).label || 'Data tujuan'}
            </Text>
            {!!getDigitalRewardDestForm(coupon).hint ? (
              <Text style={[styles.cashbackPendingDesc, { marginBottom: 8, opacity: 0.85 }]}>
                {getDigitalRewardDestForm(coupon).hint}
              </Text>
            ) : null}
            <View style={styles.rewardDestInputWrap}>
              <TextInput
                style={[styles.linkajaInput, styles.rewardDestInput, rewardOperator && styles.rewardDestInputWithBadge]}
                placeholder={getDigitalRewardDestForm(coupon).placeholder || 'No. tujuan / lainnya'}
                placeholderTextColor="#9CA3AF"
                keyboardType="default"
                inputMode="text"
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={40}
                value={rewardPhone}
                onChangeText={setRewardPhone}
                editable={!rewardSubmitting}
              />
              {rewardOperator?.logo ? (
                <View style={styles.operatorBadge} accessibilityLabel={rewardOperator.name}>
                  <Image
                    source={rewardOperator.logo}
                    style={styles.operatorBadgeLogo}
                    resizeMode="contain"
                  />
                </View>
              ) : null}
            </View>
            <TouchableOpacity
              style={[styles.cashbackSubmitBtn, { backgroundColor: '#E46B29' }, rewardSubmitting && styles.claimButtonDisabled]}
              onPress={handleCompleteDigitalReward}
              disabled={rewardSubmitting}
            >
              {rewardSubmitting ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.cashbackSubmitBtnText}>
                  {isDigitalRewardFailed(coupon)
                    ? 'Coba lagi'
                    : (getDigitalRewardDestForm(coupon).submit_label || 'Proses Voucher')}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}

        {coupon.is_claimed && isDigitalRewardProcessing(coupon) && (
          <View style={styles.cashbackSuccessContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="time-outline" size={20} color="#D97706" />
              <Text style={styles.sectionTitle}>Voucher sedang diproses</Text>
            </View>
            <Text style={styles.cashbackSuccessDesc}>
              {pollExhausted
                ? 'Masih diproses. Ketuk refresh di atas untuk cek status lagi.'
                : 'Voucher sedang diproses. Status akan diperbarui otomatis.'}
            </Text>
          </View>
        )}

        {coupon.is_claimed && isDigitalRewardCoupon(coupon) && !isDigitalRewardPending(coupon) && (
          <View style={styles.cashbackSuccessContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="checkmark-circle-outline" size={20} color="#059669" />
              <Text style={styles.sectionTitle}>Voucher berhasil diproses</Text>
            </View>
            <Text style={styles.cashbackSuccessDesc}>
              Voucher sudah diproses. Simpan kode referensi di bawah bila diperlukan.
            </Text>
          </View>
        )}

        {coupon.is_claimed && isDigitalRewardCoupon(coupon) && getDigitalRewardSn(coupon) && !!coupon.tampil_barcode && (
          <View style={styles.qrCodeContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="barcode-outline" size={20} color="#6B7280" />
              <Text style={styles.sectionTitle}>Barcode kasir</Text>
            </View>
            <View style={styles.qrCodeWrapper}>
              <CashierBarcode value={getDigitalRewardSn(coupon)} />
              <Text style={styles.qrCodeHint}>Tunjukkan barcode ini ke kasir</Text>
            </View>
          </View>
        )}

        {coupon.is_claimed && isDigitalRewardCoupon(coupon) && getDigitalRewardSn(coupon) && (
          <View style={styles.couponCodeContainer}>
            <View
              style={[
                styles.couponCodeBadge,
                String(getDigitalRewardSn(coupon)).length > 40 && styles.couponCodeBadgeLong,
              ]}
            >
              <Text
                style={[
                  styles.couponCodeText,
                  String(getDigitalRewardSn(coupon)).length > 40 && styles.couponCodeTextLong,
                ]}
                selectable
              >
                {getDigitalRewardSn(coupon)}
              </Text>
            </View>
          </View>
        )}

        {coupon.is_claimed && !isProductGiftCoupon(coupon) && !isCashbackCoupon(coupon) && !isDigitalRewardCoupon(coupon) && (
          <View style={styles.couponCodeContainer}>
            <View style={styles.couponCodeBadge}>
              <Text style={styles.couponCodeText}>{coupon.kode_kupon || coupon.code || 'NO CODE'}</Text>
            </View>
          </View>
        )}

        {/* QR Code Display for Indobest Coupons */}
        {coupon.is_claimed && coupon.isIndobest && (
          <View style={styles.qrCodeContainer}>
            <View style={styles.sectionHeader}>
              <Ionicons name="qr-code-outline" size={20} color="#6B7280" />
              <Text style={styles.sectionTitle}>Kode QR Indobest</Text>
            </View>
            <View style={styles.qrCodeWrapper}>
              <QRCode
                value={coupon.externalId || coupon.kode_kupon || coupon.code}
                size={200}
                color="#000000"
                backgroundColor="#FFFFFF"
              />
              <Text style={styles.qrCodeHint}>
                Tunjukkan QR ini di kasir Indobest
              </Text>
            </View>
          </View>
        )}

        {/* Terms and Conditions */}
        <View style={styles.sectionContainer}>
          <View style={styles.sectionHeader}>
            <Ionicons name="list-outline" size={20} color="#6B7280" />
            <Text style={styles.sectionTitle}>Syarat & Ketentuan</Text>
          </View>
          <View style={styles.termsContainer}>
            <Text style={styles.sectionContent}>
              {coupon.syarat_ketentuan || coupon.termsConditions ? 
                (coupon.syarat_ketentuan || coupon.termsConditions)
                  .replace(/\\n/g, '\n')
                  .split('\n')
                  .filter(line => line.trim())
                  .map(line => line.startsWith('•') ? line : `• ${line}`)
                  .join('\n') : 
                `Tidak ada syarat dan ketentuan`
              }
            </Text>
          </View>
        </View>

        {coupon.is_claimed && isProductGiftCoupon(coupon) && getHadiahProdukValidationCode(coupon) ? (
          <TouchableOpacity
            style={styles.copyButton}
            onPress={() => handleCopyCouponCode(getHadiahProdukValidationCode(coupon))}
          >
            <Ionicons name="copy-outline" size={20} color="#059669" />
            <Text style={styles.copyButtonText}>Salin Kode Validasi</Text>
          </TouchableOpacity>
        ) : coupon.is_claimed && isDigitalRewardCoupon(coupon) && getDigitalRewardSn(coupon) ? (
          <TouchableOpacity
            style={styles.copyButton}
            onPress={() => handleCopyCouponCode(getDigitalRewardSn(coupon))}
          >
            <Ionicons name="copy-outline" size={20} color="#059669" />
            <Text style={styles.copyButtonText}>Salin Kode</Text>
          </TouchableOpacity>
        ) : coupon.is_claimed && isDigitalRewardPending(coupon) ? (
          null
        ) : coupon.is_claimed && !isProductGiftCoupon(coupon) && !isCashbackPending(coupon) && !isDigitalRewardCoupon(coupon) ? (
          <TouchableOpacity
            style={styles.copyButton}
            onPress={() => handleCopyCouponCode(coupon.kode_kupon || coupon.code)}
          >
            <Ionicons name="copy-outline" size={20} color="#059669" />
            <Text style={styles.copyButtonText}>Copy Code</Text>
          </TouchableOpacity>
        ) : !coupon.is_claimed ? (
          <TouchableOpacity
            style={[styles.claimButton, isLoading && styles.claimButtonDisabled]}
            onPress={handleClaimCoupon}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#059669" size="small" />
            ) : (
              <>
                <Ionicons name="copy-outline" size={20} color="#059669" />
                <Text style={styles.claimButtonText}>Klaim Voucher</Text>
              </>
            )}
          </TouchableOpacity>
        ) : null}
      </View>
      </ScrollView>
      
      {customAlertModal}
      {paygiftBlockedModal}

    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  imageContainer: {
    height: 160,
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    marginHorizontal: 16,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  couponImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  imageContent: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  couponInfo: {
    alignItems: 'center',
  },
  discountBadge: {
    backgroundColor: '#F59E0B',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 8,
  },
  discountLabel: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  discountAmount: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  minTransaction: {
    color: '#FFFFFF',
    fontSize: 14,
    opacity: 0.9,
  },
  decorativeCircle: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#FFFFFF',
    opacity: 0.25,
  },
  ticketIcon: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    alignItems: 'center',
  },
  decorativeDots: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 4,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  content: {
    padding: 16,
  },
  badgesContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  badgeText: {
    fontSize: 12,
    color: '#059669',
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  indobestBadge: {
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  indobestBadgeText: {
    color: '#D97706',
    fontWeight: '600',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 24,
    lineHeight: 28,
    fontFamily: 'Poppins-Bold',
  },
  detailsContainer: {
    gap: 16,
    marginBottom: 32,
  },
  detailItem: {
    gap: 8,
  },
  detailHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  detailValue: {
    fontSize: 16,
    color: '#1F2937',
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
  },
  claimButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#059669',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 8,
  },
  claimButtonDisabled: {
    opacity: 0.5,
  },
  claimButtonText: {
    fontSize: 14,
    color: '#059669',
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    fontFamily: 'Poppins-SemiBold',
  },
  sectionContent: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    fontFamily: 'Poppins',
  },
  termsContainer: {
    gap: 8,
  },
  termItem: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#059669',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  copyButtonText: {
    fontSize: 16,
    color: '#059669',
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  couponCodeContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  couponCodeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#059669',
    gap: 8,
    marginBottom: 12,
  },
  couponCodeBadgeLong: {
    width: '100%',
    alignItems: 'flex-start',
  },
  couponCodeText: {
    fontSize: 16,
    color: '#059669',
    fontWeight: '600',
    letterSpacing: 1,
    fontFamily: 'Poppins-SemiBold',
  },
  couponCodeTextLong: {
    fontSize: 12,
    letterSpacing: 0,
    lineHeight: 18,
    flexShrink: 1,
    width: '100%',
  },
  qrCodeContainer: {
    alignItems: 'center',
    marginBottom: 24,
    backgroundColor: '#F9FAFB',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  qrCodeWrapper: {
    alignItems: 'center',
    gap: 16,
    marginTop: 12,
  },
  qrCodeHint: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
  cashbackPendingContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  cashbackPendingDesc: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 12,
    fontFamily: 'Poppins',
  },
  cashbackSuccessContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#ECFDF5',
    borderWidth: 1,
    borderColor: '#6EE7B7',
  },
  cashbackSuccessDesc: {
    fontSize: 12,
    color: '#374151',
    lineHeight: 20,
    fontFamily: 'Poppins',
  },
  cashbackRef: {
    marginTop: 10,
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'Poppins',
  },
  linkajaInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 14,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
    fontFamily: 'Poppins',
  },
  rewardDestInputWrap: {
    position: 'relative',
    marginBottom: 12,
  },
  rewardDestInput: {
    marginBottom: 0,
  },
  rewardDestInputWithBadge: {
    paddingRight: 52,
  },
  operatorBadge: {
    position: 'absolute',
    right: 8,
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  operatorBadgeLogo: {
    width: 32,
    height: 32,
  },
  cashbackSubmitBtn: {
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  cashbackSubmitBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
  },
});

export default CouponDetailsScreen;